import { useTranslation } from "react-i18next"
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import { useEffect, useRef, useState } from "react"
import { fetchOrder, fetchPickupLocations, convertToClickAndCollect } from "../services/orderServices"
import { resolveCncErrorCode } from "../utils/cncErrorCodes"
import { isCncUpsellEligible } from "../utils/cncUpsellEligibility"
import { Loader } from "../components/Loader"
import { LogoHeader } from "../components/LogoHeader"
import { NavBar } from "../components/NavBar"
import { Button } from "../components/Button"
import { PATH_HOME } from "../navigation/RouterPaths"

/**
 * Parse service_name to extract display name and distance.
 * Formats:
 *   "Recolecta en punto físico a 1.2km - Estafeta Av. Reforma"
 *   "Recolecta en punto físico a 0.8km -  DHL - DHL ServicePoint Centro"
 *   "Recolecta en punto físico -  Punto Post - Rockwell electronics - Lun, Mar, ...: 10:00 - 20:00"
 * Fallback: returns raw service_name if parsing fails.
 */
const parseServiceName = (serviceName) => {
    try {
        const distanceMatch = serviceName.match(/(\d+\.?\d*)\s*km/i)
        const distance = distanceMatch ? `${distanceMatch[1]}km` : null

        // Strip the "Recolecta en punto físico [a X.Xkm]" prefix by finding the first " - "
        const firstDash = serviceName.indexOf(' - ')
        let carrierPart = firstDash !== -1
            ? serviceName.substring(firstDash + 3).trim()
            : serviceName

        // Strip schedule info (day abbreviations followed by times, e.g. "Lun, Mar, ...: 10:00 - 20:00")
        const scheduleMatch = carrierPart.match(/\s*-\s*(Lun|Mar|Mie|Jue|Vie|Sab|Dom|Mon|Tue|Wed|Thu|Fri|Sat|Sun)[,:\s]/i)
        if (scheduleMatch) {
            carrierPart = carrierPart.substring(0, scheduleMatch.index).trim()
        }

        return { displayName: carrierPart || serviceName, distance }
    } catch {
        // Never undefined: the render sites fall back to the point's service_code.
        return { displayName: serviceName || '', distance: null }
    }
}

export const ClickAndCollectContainer = () => {
    const { i18n } = useTranslation()
    const { order_id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [data, setData] = useState(location?.state?.data)
    const [step, setStep] = useState('loading') // loading | list | empty | fetchError | confirm | converting | success | error
    const [pickupPoints, setPickupPoints] = useState([])
    const [selectedPoint, setSelectedPoint] = useState(null)
    const [errorMessage, setErrorMessage] = useState('')
    // Whether the failure that produced the current fetchError screen is worth retrying.
    // Kept separate from errorMessage so the copy and the retry can disagree.
    const [canRetry, setCanRetry] = useState(false)
    // Bumped by handleRetry to re-run the pickup-locations effect after a transient failure.
    const [reloadToken, setReloadToken] = useState(0)

    // Fetch order if not passed via location.state
    const { isLoading: isOrderLoading, isError: isOrderError, data: dataFromQuery } = useQuery(
        [`order${order_id}`, order_id],
        () => fetchOrder(order_id),
        {
            retry: 1,
            enabled: !data
        }
    )

    useEffect(() => {
        if (dataFromQuery) {
            setData(dataFromQuery)
        }
    }, [dataFromQuery])

    // Fetch pickup locations once order data is available
    useEffect(() => {
        if (!data?.order) return

        const order = data.order
        // This effect still runs on the render that returns the <Navigate> guard below,
        // so an ineligible order must be stopped here too — it never triggers a search.
        if (!isCncUpsellEligible(order)) return

        const postalCode = order.shipping?.zip_code
        const address = [order.shipping?.address_1, order.shipping?.city, order.shipping?.state]
            .filter(Boolean)
            .join(', ')

        // The effect re-runs on every reloadToken bump, so a superseded (or post-unmount)
        // response must not write state and must never navigate a user who already left.
        let cancelled = false

        const loadPickupLocations = async () => {
            try {
                const response = await fetchPickupLocations(postalCode, address, order_id)
                if (cancelled) return

                if (response?.cnc_eligible === false) {
                    const resolved = resolveCncErrorCode(response.code)
                    setCanRetry(false)
                    if (dispatchCncResolved(resolved)) return
                    // Unmapped or absent code: still a server rejection, so say what is true
                    // rather than promising a retry that is not offered.
                    setErrorMessage(i18n.t('clickAndCollect.error_not_eligible_generic'))
                    setStep('fetchError')
                    return
                }

                const rates = response?.rates || []
                // A rate without a usable service_code is a payload defect, not a transient
                // failure: drop it here instead of letting it throw into the catch below.
                const usableRates = rates.filter((rate) => typeof rate?.service_code === 'string' && rate.service_code.length > 0)
                // A payload where no rate has a usable service_code is a server-shape defect,
                // not an empty result: show the generic error (the retry is harmless) rather
                // than a false "no points near you".
                if (rates.length > 0 && usableRates.length === 0) {
                    if (cancelled) return
                    setErrorMessage('')
                    setCanRetry(true)
                    setStep('fetchError')
                    return
                }
                // Keep only the closest point per carrier (first occurrence wins)
                const CARRIER_PREFIXES = ['EST', 'DHL', 'PUNTOPOST']
                const seenCarriers = new Set()
                const deduplicated = usableRates.filter((rate) => {
                    const code = rate.service_code.replace(/^CUBBOCNC/, '')
                    const carrier = CARRIER_PREFIXES.find(p => code.startsWith(p)) || code
                    if (seenCarriers.has(carrier)) return false
                    seenCarriers.add(carrier)
                    return true
                })
                if (deduplicated.length === 0) {
                    setStep('empty')
                } else {
                    setPickupPoints(deduplicated)
                    setStep('list')
                }
            } catch (err) {
                if (cancelled) return
                // thrown = transient: clear any mapped message so the retry is offered
                setErrorMessage('')
                setCanRetry(true)
                setStep('fetchError')
            }
        }

        loadPickupLocations()

        return () => { cancelled = true }
    }, [data, reloadToken])

    const handleSelect = (point) => {
        setSelectedPoint(point)
    }

    const handleContinue = () => {
        if (selectedPoint) {
            setStep('confirm')
        }
    }

    const handleGoBack = () => {
        setSelectedPoint(null)
        setStep('list')
    }

    // Dispatch a resolved cnc-error action. Returns true if dispatched, false otherwise.
    // Caller falls through to its own handling when this returns false.
    const dispatchCncResolved = (resolved) => {
        if (resolved?.action === 'redirect_home') {
            navigate('/', { replace: true })
            return true
        }
        if (resolved?.action === 'redirect_tracking') {
            navigate(`/orders/${order_id}`, { replace: true })
            return true
        }
        if (resolved?.action === 'message') {
            setErrorMessage(i18n.t(resolved.translationKey))
            setCanRetry(false)
            setStep('fetchError')
            return true
        }
        return false
    }

    // handleConfirm awaits the conversion, so the user can leave (NavBar, device Back)
    // while it is in flight. Same invariant as the pickup-locations effect: a late answer
    // must neither write state nor navigate someone who is no longer on this screen.
    const mountedRef = useRef(true)
    // Set on mount (not only at ref init) so StrictMode's simulated unmount/remount in
    // development does not leave the ref false for the component's whole life.
    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    // Write the converted state into the tracking page's cached order (only if that
    // entry exists — setQueryData on a missing key would create a success/undefined
    // entry and blank the tracking page), then invalidate so it refetches the truth.
    const markOrderConvertedInCache = () => {
        const key = ['order', order_id]
        if (queryClient.getQueryData(key) !== undefined) {
            queryClient.setQueryData(key, (old) => (old?.order ? { ...old, order: { ...old.order, is_click_and_collect: true } } : old))
        }
        queryClient.invalidateQueries(key)
    }

    const handleConfirm = async () => {
        setStep('converting')
        setErrorMessage('')

        try {
            await convertToClickAndCollect(order_id, selectedPoint.service_code)
            // The conversion is done server-side, so the cache is updated either way; only
            // the screen change is skipped for a user who already left.
            markOrderConvertedInCache()
            // HelpOrderContainer uses [`order${order_id}`, order_id]. This page's own query
            // reads that key too, so it is invalidated but never written: writing it would
            // feed the eligibility guard below and bounce the user off the success screen.
            queryClient.invalidateQueries([`order${order_id}`, order_id])
            if (!mountedRef.current) return
            setStep('success')
        } catch (err) {
            if (!mountedRef.current) return
            const errorPayload = err?.response?.data || {}
            const newCode = errorPayload.code || ''
            const errorCode = errorPayload.error || ''

            if (dispatchCncResolved(resolveCncErrorCode(newCode))) return

            if (errorCode === 'INVALID_ORDER_ID') {
                navigate('/', { replace: true })
                return
            }
            if (errorCode === 'ALREADY_CLICK_AND_COLLECT') {
                // The order is CnC after all: refresh the tracking page's copy so it does
                // not keep offering the upsell.
                markOrderConvertedInCache()
                navigate(`/orders/${order_id}`, { replace: true })
                return
            }
            if (errorCode === 'INVALID_ORDER_STATUS') {
                setErrorMessage(i18n.t('clickAndCollect.error_order_status'))
                setStep('error')
                return
            }
            if (errorCode === 'INVALID_CNC_SERVICE_CODE' || errorCode === 'CNC_SHIPPING_METHOD_NOT_FOUND') {
                // Go back to list so user can pick a different point
                setSelectedPoint(null)
                setErrorMessage(i18n.t('clickAndCollect.error_generic'))
                setStep('list')
                return
            }
            setErrorMessage(i18n.t('clickAndCollect.error_generic'))
            setStep('error')
        }
    }

    // Back from a terminal (empty / ineligible / failed) pickup screen. When the user
    // arrived from the tracking page, pop that entry so device Back keeps working;
    // on a deep link (no in-app history, location.key === 'default') replace instead,
    // so Back never lands on a dead pickup screen (eng review OV6).
    const handleBackToTracking = () => {
        if (location.key !== 'default') {
            navigate(-1)
        } else {
            navigate(`/orders/${order_id}`, { replace: true })
        }
    }

    // After a conversion the pickup screen is stale, so it must not be revisitable:
    // always replace, never pop.
    const handleBackToTrackingAfterConversion = () => {
        navigate(`/orders/${order_id}`, { replace: true })
    }

    // Only offered on the transient (thrown) failure path; mapped-code rejections are permanent.
    const handleRetry = () => {
        setErrorMessage('')
        setCanRetry(false)
        setStep('loading')
        setReloadToken((t) => t + 1)
    }

    const handleSelectDifferent = () => {
        setSelectedPoint(null)
        setErrorMessage('')
        setStep('list')
    }

    // Loading states
    if (isOrderLoading || (!data && !isOrderError)) {
        return <Loader className="bg-gray-50" show={true} />
    }

    if (isOrderError) {
        return <Navigate to={PATH_HOME} replace={true} />
    }

    const order = data?.order
    const logoSrc = order?.store?.logo_file?.path && process.env.REACT_APP_PLATFORM_URL + order.store.logo_file.path

    // Guard: the same gate the upsell card uses, so a bookmarked or reloaded pickup URL
    // cannot reach a dead end the card was hidden to avoid. Converting and converted
    // orders are exempt: the conversion itself makes them ineligible, and the user has
    // to be able to finish the flow and read the confirmation.
    if (order && step !== 'converting' && step !== 'success' && !isCncUpsellEligible(order)) {
        return <Navigate to={`/orders/${order_id}`} replace={true} />
    }

    // Render loading step (fetching pickup locations)
    const renderLoading = () => (
        <div className="flex flex-col items-center justify-center py-20">
            <Loader show={true} />
            <div className="text-sm text-gray-400 mt-4">{i18n.t('clickAndCollect.searching')}</div>
        </div>
    )

    // Both terminal screens share the same exit: a reassurance line (the order still
    // ships to the customer's address) and a primary button back to tracking.
    // Decided in the 2026-09-06 design review — the upsell card sends more people here.
    const renderTerminalFooter = () => (
        <>
            <div className="text-sm text-gray-500 text-center mt-2">{i18n.t('clickAndCollect.no_locations_reassurance')}</div>
            <Button label={i18n.t('clickAndCollect.back_to_tracking')} onClick={handleBackToTracking} />
        </>
    )

    // Render empty state
    const renderEmpty = () => (
        <div className="flex flex-col items-center justify-center py-20 px-10">
            <div className="text-sm font-medium text-gray-700">{i18n.t('clickAndCollect.no_locations')}</div>
            <div className="text-sm text-gray-400 text-center mt-2">{i18n.t('clickAndCollect.no_locations_description')}</div>
            {renderTerminalFooter()}
        </div>
    )

    // Render fetch error / "can't proceed" terminal screen.
    //   canRetry → thrown error (network / 5xx): transient, retry offered above the footer
    //   otherwise a server rejection (mapped or not): permanent, footer only
    const renderFetchError = () => (
        <div className="flex flex-col items-center justify-center py-20 px-10">
            <div className="text-sm text-gray-400 text-center">{errorMessage || i18n.t('clickAndCollect.error_fetch')}</div>
            {canRetry && (
                <Button
                    variant="outline"
                    size="md"
                    className="mt-5"
                    label={i18n.t('clickAndCollect.try_again')}
                    onClick={handleRetry}
                />
            )}
            {renderTerminalFooter()}
        </div>
    )

    // Render pickup points list
    const renderList = () => (
        <div className="flex flex-col py-5 px-10">
            <div className="text-sm text-gray-500 mb-4">
                {i18n.t('clickAndCollect.locations_found', {
                    count: pickupPoints.length,
                    postal_code: order?.shipping?.zip_code
                })}
            </div>
            <div className="flex flex-col space-y-3">
                {pickupPoints.map((point) => {
                    const { displayName, distance } = parseServiceName(point.service_name)
                    const isSelected = selectedPoint?.service_code === point.service_code
                    return (
                        <button
                            key={point.service_code}
                            className={`text-left text-sm p-3 border rounded-lg bg-white shadow-sm
                                ${isSelected ? 'border-2 border-blue-500' : 'border-gray-300'}`}
                            onClick={() => handleSelect(point)}
                        >
                            <div className="font-semibold text-gray-800">{displayName || point.service_code}</div>
                            <div className="text-gray-500 mt-1">{point.source}</div>
                            {distance && <div className="text-gray-500 mt-1">{distance}</div>}
                        </button>
                    )
                })}
            </div>
            <button
                className={`py-3 px-5 mt-5 w-full border border-transparent shadow-sm rounded-lg text-white
                    ${selectedPoint ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-500 opacity-50 cursor-not-allowed'}`}
                disabled={!selectedPoint}
                onClick={handleContinue}
            >
                {i18n.t('clickAndCollect.continue')}
            </button>
        </div>
    )

    // Render confirmation step
    const renderConfirm = () => {
        const { displayName } = parseServiceName(selectedPoint.service_name)
        return (
            <div className="flex flex-col py-5 px-10">
                <div className="text-sm text-gray-500 mb-4">{i18n.t('clickAndCollect.confirm_description')}</div>
                <div className="border border-gray-300 rounded-lg bg-white p-3 shadow-sm">
                    <div className="font-semibold text-sm text-gray-800">{displayName || selectedPoint.service_code}</div>
                    <div className="text-sm text-gray-500 mt-1">{selectedPoint.source}</div>
                </div>
                <button
                    className="py-3 px-5 mt-5 w-full border border-transparent shadow-sm rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                    onClick={handleConfirm}
                >
                    {i18n.t('clickAndCollect.confirm')}
                </button>
                <div className="text-center mt-3">
                    <span className="cursor-pointer text-blue-500 hover:text-blue-400 text-sm" onClick={handleGoBack}>
                        {i18n.t('clickAndCollect.go_back')}
                    </span>
                </div>
            </div>
        )
    }

    // Render converting (loading during API call)
    const renderConverting = () => {
        const { displayName } = parseServiceName(selectedPoint.service_name)
        return (
            <div className="flex flex-col py-5 px-10">
                <div className="text-sm text-gray-500 mb-4">{i18n.t('clickAndCollect.confirm_description')}</div>
                <div className="border border-gray-300 rounded-lg bg-white p-3 shadow-sm">
                    <div className="font-semibold text-sm text-gray-800">{displayName || selectedPoint.service_code}</div>
                    <div className="text-sm text-gray-500 mt-1">{selectedPoint.source}</div>
                </div>
                <button
                    className="py-3 px-5 mt-5 w-full border border-transparent shadow-sm rounded-lg bg-blue-500 text-white opacity-50 cursor-not-allowed"
                    disabled={true}
                >
                    {i18n.t('clickAndCollect.confirm')}
                </button>
            </div>
        )
    }

    // Render success screen
    const renderSuccess = () => {
        const { displayName } = parseServiceName(selectedPoint.service_name)
        return (
            <div className="flex flex-col items-center py-10 px-10">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <div className="font-semibold text-gray-800">{i18n.t('clickAndCollect.success_title')}</div>
                <div className="text-sm text-gray-500 mt-2">{i18n.t('clickAndCollect.success_description')}</div>
                <div className="border border-gray-300 rounded-lg bg-white p-3 shadow-sm mt-4 w-full">
                    <div className="font-semibold text-sm text-gray-800">{displayName || selectedPoint.service_code}</div>
                    <div className="text-sm text-gray-500 mt-1">{selectedPoint.source}</div>
                </div>
                <button
                    className="py-3 px-5 mt-5 w-full border border-transparent shadow-sm rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                    onClick={handleBackToTrackingAfterConversion}
                >
                    {i18n.t('clickAndCollect.back_to_tracking')}
                </button>
            </div>
        )
    }

    // Render error screen
    const renderError = () => {
        const { displayName } = parseServiceName(selectedPoint.service_name)
        return (
            <div className="flex flex-col py-5 px-10">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <div className="text-sm text-red-600">{errorMessage}</div>
                </div>
                <div className="border border-gray-300 rounded-lg bg-white p-3 shadow-sm">
                    <div className="font-semibold text-sm text-gray-800">{displayName || selectedPoint.service_code}</div>
                    <div className="text-sm text-gray-500 mt-1">{selectedPoint.source}</div>
                </div>
                <button
                    className="py-3 px-5 mt-5 w-full border border-transparent shadow-sm rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                    onClick={handleConfirm}
                >
                    {i18n.t('clickAndCollect.try_again')}
                </button>
                <div className="text-center mt-3">
                    <span className="cursor-pointer text-blue-500 hover:text-blue-400 text-sm" onClick={handleSelectDifferent}>
                        {i18n.t('clickAndCollect.select_different')}
                    </span>
                </div>
            </div>
        )
    }

    // Determine NavBar title based on step
    const navTitle = step === 'fetchError'
        ? i18n.t('clickAndCollect.unavailable_title')
        : step === 'confirm' || step === 'converting' || step === 'error'
            ? i18n.t('clickAndCollect.confirm_title')
            : i18n.t('clickAndCollect.title')

    // Hide NavBar on success screen
    const showNavBar = step !== 'success'

    return (
        <>
            <div className="bg-gray-50 py-5 px-10 sm:max-w-[480px]">
                <LogoHeader src={logoSrc} />
            </div>
            <div className="flex flex-col bg-gray-50">
                {showNavBar && (
                    <NavBar
                        title={navTitle}
                        className="mt-5"
                        onClick={() => {
                            if (step === 'confirm' || step === 'error') {
                                handleGoBack()
                            } else {
                                handleBackToTracking()
                            }
                        }}
                    />
                )}
                {step === 'loading' && renderLoading()}
                {step === 'list' && renderList()}
                {step === 'empty' && renderEmpty()}
                {step === 'fetchError' && renderFetchError()}
                {step === 'confirm' && renderConfirm()}
                {step === 'converting' && renderConverting()}
                {step === 'success' && renderSuccess()}
                {step === 'error' && renderError()}
            </div>
        </>
    )
}
